import { CANVAS_ARROW_HIGHLIGHT_COLOR, CANVAS_ITEM_TEXT_FONT, CANVAS_ITEM_TEXT_FONT_WEIGHT, COLOR_13, COLOR_21 } from "../styles/Canvas";
import { Shape } from "../enums/Shape";
import { HeaderTextValues } from "../interfaces/HeaderTextValues";
import { ShapeBounding } from "./ShapeBounding";
import { DrawShapeFunction } from "../types/DrawShapeFunction";
import { AbstractShape } from "../abstract/Shape";
import { Values } from "./Values";
import { CanvasColorTheme } from "../interfaces/CanvasColorTheme";
import { ComposedMetrics } from "./ComposedMetrics";
import { TimeUnit } from "../enums/TimeUnit";
import { secondsTo, TimeSuffix } from "../utils/utils";
import { BoxMetrics } from "../interfaces/BoxMetrix";

export class CanvasItem extends AbstractShape{

    constructor(
        id: string,
        zIndex: number,
        parent: AbstractShape | undefined = undefined
    ){
        super(id, zIndex, parent);
    }

    borderWidth: number = 3;
    scale: number = 1;

    override draw(ctx: CanvasRenderingContext2D) {

        // let text = this.name;
        this.currentBounding = null;
        this.scale = Math.min(this.width, this.height);
        this.borderWidth = (Math.min(this.width, this.height) / 100) * 3;
        
        this.radius = this.borderWidth*2;
        //draw box
        this.renderShape(ctx, this.name);

        //draw handle
        if(this.focus) this.drawHandle(ctx);

        // Draw children
        this.drawChildrens(ctx);
    }

    override drawOnTop(ctx: CanvasRenderingContext2D) {
        if(this.showShadow && this.isDragging){
            this.x = this.shadowPos.x;
            this.y = this.shadowPos.y;
            let shadowTheme = COLOR_21;
            this.renderShape(ctx, this.name, shadowTheme);
            // this.drawChildrens(ctx, true);
            this.x = this.originalPos.x;
            this.y = this.originalPos.y;
        }
        this.drawChildrens(ctx, true);
    }

    drawChildrens(ctx: CanvasRenderingContext2D, onTop: boolean = false){
        ctx.save();
        ctx.translate(this.x, this.y); // Translate context to the parent's coordinate system
        if(!this.fill){
            for (const child of this.children) {
                if(!onTop){
                    child.draw(ctx);
                }else{
                    child.drawOnTop(ctx);
                }
            }
        }
        ctx.restore();
    }

    renderShape(ctx: CanvasRenderingContext2D, text: string, theme?: CanvasColorTheme | undefined){
        switch(this.shape){
            case Shape.RECTANGLE:
                this.drawShape(ctx, text, this.drawBox, theme);
                break;
            case Shape.CIRCLE:
                this.drawShape(ctx, text, this.drawCircle, theme);
                break;
            case Shape.HEXAGON:
                this.drawShape(ctx, text, this.drawHexagon, theme);
                break;
            case Shape.RHOMBUS:
                // this.drawShape(ctx, text, this.drawRhombus);
                this.drawRhombusShape(ctx, text, theme);
                break;
            case Shape.INFO_RECTANGLE:
                this.drawInfoRectangle(ctx, text, theme);
                break;
            case Shape.SIMPLE_RECTANGLE:
                this.drawSimpleShape(ctx, text, this.drawBox, 1.6, undefined, theme);
                break;
            case Shape.SIMPLE_CIRCLE:
                this.drawSimpleShape(ctx, text, this.drawCircle, 1.6, undefined, theme);
                break;
            case Shape.SIMPLE_RHOMBUS:
                this.drawSimpleShape(ctx, text, this.drawRhombus, 1.5, 2.8, theme);
                break;
            case Shape.SIMPLE_HEXAGON:
                this.drawSimpleShape(ctx, text, this.drawHexagon, 1.6, undefined, theme);
                break;
            case Shape.SWIM_LANE_CONTAINER:
                this.drawSwinLaneContainer(ctx, text, undefined, undefined, theme);
                break;
            case Shape.SWIM_LANE_DOTTED:
                this.drawSwimLaneDotted(ctx, text, undefined, undefined, theme);
                break;
            case Shape.DOTTED_AREA:
                this.drawDottedBox(ctx, text, undefined, theme);
                break;
            case Shape.RECTANGLE_AREA:
                this.drawAreaContainer(ctx, text, undefined, theme);
                break;
            case Shape.PEOPLE_HEX:
                let position_holder = "";
                if(this.dbPropierties){
                    if(this.dbPropierties.position_holder)position_holder = this.dbPropierties.position_holder;
                }
                this.drawPeopleHex(ctx, position_holder, text, theme);
                // this.drawPeopleHex(ctx, this.dbPropierties.position_holder, this.dbPropierties.position_holder);
                break;
            case Shape.SIMPLE_AREA:
                this.drawSimpleArea(ctx, text, undefined, theme);
                break;
            case Shape.ROMBOID_LEFT:
                this.drawNoGlowShape(ctx, text, this.drawRomboidLeft, theme);
                break;
            case Shape.ROMBOID_RIGHT:
                this.drawNoGlowShape(ctx, text, this.drawRomboidRight, theme);
                break;
            case Shape.TRAPEZIUM_LEFT:
                this.drawNoGlowShape(ctx, text, this.drawTrapeziumLeft, theme);
                break;
            case Shape.TRAPEZIUM_RIGHT:
                this.drawNoGlowShape(ctx, text, this.drawTrapeziumRight, theme);
                break;
            case Shape.SOLID_TEXT_RECTANGLE:
                this.drawNoGlowShape(ctx, text, this.drawSolidBox, theme);
                break;
            default:
                this.drawShape(ctx, text, this.drawBox, theme);
                break;
        }
    }

    private drawInfoRectangle(
        ctx: CanvasRenderingContext2D, 
        text: string,
        theme?: CanvasColorTheme | undefined
    ) {
        if (this.focus) this.drawBox(ctx, undefined, CANVAS_ARROW_HIGHLIGHT_COLOR, this.borderWidth, CANVAS_ARROW_HIGHLIGHT_COLOR, undefined, undefined, undefined, undefined, theme);
        this.drawBox(ctx, 0, undefined, undefined, undefined, undefined, undefined, undefined, undefined, theme);
        if (text) {
            let headerValues = this.calculateTextNoLines(text, undefined, this.width/10, false);
            let innerSpacingX = (this.scale/67) * 2;
            let textSpacing = (this.scale/67) * 5;
            headerValues.startY+=innerSpacingX;
            headerValues.totalHeight+=textSpacing;
            this.drawHeaderBox(ctx, undefined, "#00000000", headerValues.totalHeight, theme);
            let headerBounding = this.toShapeBounding();
            
            headerBounding.x = headerBounding.x + textSpacing;
            this.drawText(ctx, text, headerValues, headerBounding, undefined, false, theme);
            let innerSpacingY = (this.scale/67) * 4;
            let strokeSize = (this.scale/67)*0.4;
            let boudingInnerBorder = new ShapeBounding(this.x + innerSpacingX, this.y + headerValues.totalHeight + innerSpacingX, this.width - innerSpacingY, (this.height - innerSpacingY) - headerValues.totalHeight);
            this.drawInnerBorder(ctx, strokeSize, undefined, boudingInnerBorder, undefined, theme);

            if(this.dbData){
                let contentValues = this.calculateText(ctx, this.dbData.properties.__EDT_UUID__, undefined, this.width/17, false);
                contentValues.startY += headerValues.totalHeight;
                let subTextBounding = this.toShapeBounding();
                subTextBounding.x = subTextBounding.x + textSpacing;
                this.drawText(ctx, this.dbData.properties.__EDT_UUID__, contentValues, subTextBounding, this.theme_color.subTextColor, false, theme);
            }
        }
        let IR_width = this.width / 3.5;
        let IR_height = this.width / 3.5;
        let IR_X = this.x + this.width - IR_width;
        let IR_Y = this.y + this.height - IR_height;
        let bounding = new ShapeBounding(IR_X, IR_Y, IR_width, IR_height);
        this.drawIcon(ctx, bounding, 1.7, undefined, theme);
        // this.chooseRenderMetricsOrSimulation(ctx);
        this.renderMetrics(ctx);
        this.renderSimulation(ctx);
    }

    // private chooseRenderMetricsOrSimulation(ctx: CanvasRenderingContext2D, bounding: ShapeBounding = this.toShapeBounding()){
    //     if(this.showMetrics){
    //         this.renderMetrics(ctx, bounding);
    //         return;
    //     }
    //     this.renderSimulation(ctx, bounding);
    // }

    private renderSimulation(ctx: CanvasRenderingContext2D, bounding: ShapeBounding = this.toShapeBounding()){
        if(this.simulationValues){
            if (!this.simulationValues.no_worker) {
                let workerValue = this.simulationValues.workersSlider.value.toString();
                this.drawTopRightCircle(ctx, workerValue, bounding);
            }

            let timeValue = this.simulationValues.timeSlider.value + "%";
            this.drawCenterRightCircle(ctx, timeValue, bounding);

            let waitValue = this.simulationValues.waitSlider.value.toString();
            this.drawBottomRightCircle(ctx, waitValue, bounding);
        }
    }

    drawTopRightCircle(ctx: CanvasRenderingContext2D, value: string, bounding: ShapeBounding = this.toShapeBounding()){
        let M_SIZE = bounding.width / 5;
        let M_X = bounding.x + bounding.width - (M_SIZE / 2);
        let M_Y = bounding.y - (M_SIZE / 2);
        let workerBounding = new ShapeBounding(M_X, M_Y, M_SIZE, M_SIZE);
        this.drawCircle(ctx, 0, undefined, undefined, undefined, workerBounding, undefined);
        let m_ct = this.calculateText(ctx, value, workerBounding, bounding.width / 3, true);
        this.drawText(ctx, value, m_ct, workerBounding, this.theme_color.subTextColor, true);
    }

    drawCenterRightCircle(ctx: CanvasRenderingContext2D, value: string, bounding: ShapeBounding = this.toShapeBounding()){
        let M_SIZE = bounding.width / 5;
        let M_X = bounding.x + bounding.width - (M_SIZE / 2);
        let M_Y = bounding.y + (bounding.height/2) - (M_SIZE / 2);
        let workerBounding = new ShapeBounding(M_X, M_Y, M_SIZE, M_SIZE);
        this.drawCircle(ctx, 0, undefined, undefined, undefined, workerBounding, undefined);
        let m_ct = this.calculateText(ctx, value, workerBounding, bounding.width / 3, true);
        this.drawText(ctx, value, m_ct, workerBounding, this.theme_color.subTextColor, true);
    }

    drawBottomRightCircle(ctx: CanvasRenderingContext2D, value: string, bounding: ShapeBounding = this.toShapeBounding()){
        let M_SIZE = bounding.width / 5;
        let M_X = bounding.x + bounding.width - (M_SIZE / 2);
        let M_Y = bounding.y + bounding.height - (M_SIZE / 2);
        let workerBounding = new ShapeBounding(M_X, M_Y, M_SIZE, M_SIZE);
        this.drawCircle(ctx, 0, undefined, undefined, undefined, workerBounding, undefined);
        let m_ct = this.calculateText(ctx, value, workerBounding, bounding.width / 3, true);
        this.drawText(ctx, value, m_ct, workerBounding, this.theme_color.subTextColor, true);
    }

    private renderMetrics(ctx: CanvasRenderingContext2D, bounding: ShapeBounding = this.toShapeBounding()){
        //metrics draw
        if(this.metrics && this.composeMetrics){
            let M_SIZE = bounding.width / 5;
            let M_X = bounding.x + bounding.width - (M_SIZE/2);
            let M_Y = bounding.y - (M_SIZE/2);
            let metricsBounding = new ShapeBounding(M_X, M_Y, M_SIZE, M_SIZE);
            this.drawCircle(ctx, 0, undefined, undefined, undefined, metricsBounding, undefined);
            // let metric = this.metrics.metric_recs.toString();
            let metric = this.selectMetricValue(this.metrics, this.composeMetrics);
            let m_ct = this.calculateText(ctx, metric, metricsBounding, bounding.width/3, true);
            this.drawText(ctx, metric, m_ct, metricsBounding, this.theme_color.subTextColor, true);
        }
    }

    private selectMetricValue(metrics: BoxMetrics, composeMetric: ComposedMetrics): string{
        let value: any = metrics[composeMetric.measure!.attribute];
        let type = composeMetric.measure!.symbol;
        if(type === "time"){
            let timeValue = composeMetric.timeUnit;
            return `${secondsTo((value as number), timeValue).toFixed(1)}${TimeSuffix[timeValue]}`;
        }
        return value+"";
    }

    private drawAreaContainer(
        ctx: CanvasRenderingContext2D,
        text: string,
        strokeColor: string = this.theme_color.strokeColor,
        theme?: CanvasColorTheme | undefined
    ){
        let thinBorder = (this.scale/300)*1.5;
        let radius = this.radius/20;
        this.ownScale = (this.scale/750);
        // Draw Focus
        if(this.focus)this.drawBox(ctx, undefined, CANVAS_ARROW_HIGHLIGHT_COLOR, thinBorder, CANVAS_ARROW_HIGHLIGHT_COLOR, undefined, radius, undefined, "#00000000", theme);
        // this.drawBox(ctx, 0, "#00000000", 1.5, undefined, undefined, 2, undefined, "#00000000");
        // Draw Shape
        this.drawBox(ctx, 0, "#00000000", thinBorder, undefined, undefined, radius, undefined, this.theme_color.transparencyBackground, theme);
        // The icon belongs to the header. A stored text size must never turn
        // its background into a solid rectangle covering the entire area.
        let size = this.icon?.length ? Math.min(this.ownScale * 65, this.width * 0.1) : 0;
        let bounding = new ShapeBounding(this.x, this.y, size, size);
        if (size > 0) {
            this.drawBox(ctx, 0, undefined, 0, undefined, bounding, 0, undefined, strokeColor, theme);
            this.drawIcon(ctx, bounding, 1.1, this.theme_color.bgColor, theme);
        }
        
        
        if(this.fill && text){
            let textRH = this.calculateText(ctx, text);
            this.drawText(ctx, text, textRH, undefined, undefined, undefined, theme);
        }
        if(!this.fill && text){
            let spacingX = this.ownScale * 10;
            let spacingY = this.ownScale * 5;
            let textBoundingHeader = this.containerTitleBounding(size + spacingX, spacingY, this.ownScale * 40);
            let calculated = this.calculateContainerTitle(ctx, text, textBoundingHeader, this.ownScale * 40);
            this.drawText(ctx, text, calculated, textBoundingHeader, undefined, false, theme);
        }
    }

    private drawSimpleArea(
        ctx: CanvasRenderingContext2D,
        text: string,
        strokeColor: string = this.theme_color.strokeColor,
        theme?: CanvasColorTheme | undefined
    ){
        this.ownScale = (this.scale/400);
        let thinBorder = (this.scale/300)*1.5;
        let radius = this.radius/20;
        if(this.focus)this.drawBox(ctx, undefined, CANVAS_ARROW_HIGHLIGHT_COLOR, thinBorder, CANVAS_ARROW_HIGHLIGHT_COLOR, undefined, radius, undefined, "#00000000", theme);
        this.drawBox(ctx, 0, "#00000000", thinBorder, undefined, undefined, radius, undefined, this.theme_color.bgColor, theme);
        
        let textRH = this.calculateText(ctx, text);
        if(this.fill && text)this.drawText(ctx, text, textRH, undefined, undefined, undefined, theme);
        if(!this.fill && text){
            let spacingX = this.ownScale * 10;
            let spacingY = this.ownScale * 5;
            let textBoundingHeader = this.containerTitleBounding(spacingX, spacingY, this.ownScale * 25);
            let calculated = this.calculateContainerTitle(ctx, text, textBoundingHeader, this.ownScale * 25);
            this.drawText(ctx, text, calculated, textBoundingHeader, undefined, false, theme);
        }
    }

    /** Container titles occupy a header band independent of the font preference. */
    private containerTitleBounding(offsetX: number, offsetY: number, legacySize: number): ShapeBounding {
        // Bound the header independently of the saved font preference. Older
        // generated Views may contain a textWidth as large as the whole card.
        const headerHeight = Math.min(this.height * 0.1, legacySize * 1.4);
        return new ShapeBounding(
            this.x + offsetX,
            this.y + offsetY,
            Math.max(1, this.width - offsetX - this.textPadding),
            Math.max(1, Math.min(this.height - offsetY, headerHeight))
        );
    }

    private calculateContainerTitle(
        ctx: CanvasRenderingContext2D,
        text: string,
        bounding: ShapeBounding,
        legacySize: number
    ): HeaderTextValues {
        const preferredSize = Math.min(this.textWidth > 0 ? this.textWidth : legacySize, bounding.height / 1.4);
        const result = this.calculateFittedText(ctx, text, bounding, preferredSize, false);
        this.realFontSize = result.fontSize;
        return result;
    }

    private drawSwinLaneContainer(
        ctx: CanvasRenderingContext2D,
        text: string,
        stroke: number = (this.scale/180)*1,
        strokeColor: string = "#87a0aa80",
        theme?: CanvasColorTheme | undefined
    ){
        if (this.focus) this.drawBox(ctx, undefined, CANVAS_ARROW_HIGHLIGHT_COLOR, this.borderWidth/2, CANVAS_ARROW_HIGHLIGHT_COLOR, undefined, 0, undefined, "#00000000", theme);
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(this.x, this.y);
        ctx.lineTo(this.x + this.width, this.y);
        ctx.moveTo(this.x, this.y + this.height);
        ctx.lineTo(this.x + this.width, this.y + this.height);
        ctx.closePath();
        ctx.strokeStyle = theme ? theme.strokeColor : strokeColor;
        ctx.lineWidth = stroke;
        ctx.stroke();
        ctx.restore();

        let realHeight = this.textWidth > 0 ? this.textWidth : this.height/2;
        let bHeight = realHeight;
        let bWidth = bHeight;
        let middleY = (this.height/2) - (bHeight/2);
        let bounding = new ShapeBounding(this.x, this.y + middleY, bWidth, bHeight);
        if(this.fill) {
            let middleX = (this.width/2) - (bWidth/2);
            bounding = new ShapeBounding(this.x + middleX, this.y + middleY, bWidth, bHeight);
        }
        // let radius = ((bounding.width + bounding.height) / 2) / 20;
        let radius = this.radius/2;
        let innerStroke = (this.scale/200) * 3;
        this.drawBox(ctx, undefined, undefined, innerStroke, undefined, bounding, radius, undefined, undefined, theme);
        let innerSpacingX = (this.scale/172) * 2;
        let innerSpacingY = (this.scale/172) * 4;
        let innerBorder = new ShapeBounding(bounding.x + innerSpacingX, bounding.y + innerSpacingX, bounding.width - innerSpacingY, bounding.height-innerSpacingY);
        let thinBorder = (this.scale/200) * 0.3;
        this.drawBox(ctx, 0, "#00000000", thinBorder, this.theme_color.innerStrokeColor, innerBorder, radius, undefined, "#00000000", theme);
        let iWidth = bWidth/1.3;
        let iHeight = bHeight/1.3;
        let innerBounding = new ShapeBounding(bounding.x + (bounding.width/2) - (iWidth/2) , bounding.y + (bounding.height/2) - (iHeight/2), iWidth, iHeight);
        let proporcion = (iHeight / 3) * 2;
        let iconBounding = new ShapeBounding(innerBounding.x, innerBounding.y, iWidth, proporcion);
        this.drawIcon(ctx, iconBounding, 1, undefined, theme);
        if(text){
            let textBounding = new ShapeBounding(bounding.x, innerBounding.y + proporcion+2, bounding.width, (iHeight / 2));
            let textRH = this.calculateText(ctx, text, textBounding);
            this.drawText(ctx, text, textRH, textBounding, undefined, undefined, theme);
        }
    }

    private drawSwimLaneDotted(
        ctx: CanvasRenderingContext2D,
        text: string,
        stroke: number = (this.scale/180)*1,
        strokeColor: string = "#87a0aa80",
        theme?: CanvasColorTheme | undefined
    ){
        if (this.focus) this.drawBox(ctx, undefined, CANVAS_ARROW_HIGHLIGHT_COLOR, this.borderWidth/2, CANVAS_ARROW_HIGHLIGHT_COLOR, undefined, 0, undefined, "#00000000", theme);
        this.realDottedSpacing = this.dottedSpacing > 0 ? this.dottedSpacing : this.scale/40;
        let dotted = new Values(this.realDottedSpacing, this.realDottedSpacing);
        let bHeight = this.height/2.5;
        let bWidth = bHeight;
        let middleY = (this.height/2) - (bHeight/2);
        let realWidth = this.textWidth > 0 ? this.textWidth : bWidth;
        let bounding = new ShapeBounding(
            this.x, 
            this.y + this.realDottedSpacing, 
            realWidth,
            this.height - this.realDottedSpacing*2
        );

        ctx.save();

        ctx.setLineDash([dotted.x, dotted.y]);
        ctx.lineDashOffset = 0;
        ctx.strokeStyle = theme ? theme.strokeColor : strokeColor;
        ctx.lineWidth = stroke;

        ctx.beginPath();
        ctx.moveTo(this.x + realWidth + this.realDottedSpacing, this.y);
        ctx.lineTo(this.x + this.width, this.y);
        ctx.closePath();
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(this.x + realWidth + this.realDottedSpacing, this.y + this.height);
        ctx.lineTo(this.x + this.width, this.y + this.height);
        ctx.closePath();
        ctx.stroke();

        ctx.restore();
        
        if(this.fill) {
            let middleX = (this.width/2) - (bWidth/2);
            bounding = new ShapeBounding(this.x + middleX, this.y + middleY, bWidth, bHeight);
        }
        let radius = this.radius/4;
        this.drawBox(ctx, 0, "#00000000", 0, "#00000000", bounding, radius, undefined, undefined, theme);
        if(text){
            if(this.fill){
                let textBounding = new ShapeBounding(bounding.x, bounding.y, bounding.width, bounding.height);
                let textRH = this.calculateText(ctx, text, textBounding);
                this.drawText(ctx, text, textRH, textBounding, undefined, undefined, theme);
            }else{
                // let textWidth = this.textWidth > 0 ? this.textWidth : bounding.width;
                let textBounding = new ShapeBounding(bounding.x, bounding.y, bounding.width, bounding.height);
                let textRH = this.calculateTextRotated(ctx, text, textBounding);
                this.drawTextRotated(ctx, text, textRH, textBounding, undefined, undefined, theme);
            }
        }
    }

    private drawDottedBox(
        ctx: CanvasRenderingContext2D,
        text: string,
        strokeColor: string = this.theme_color.strokeColor,
        theme?: CanvasColorTheme | undefined
    ){
        let thinBorder = (this.scale/100) * 0.8;
        // let dottedSpacing = (((this.height + this.width)/2)/50);
        this.realDottedSpacing = this.dottedSpacing > 0 ? this.dottedSpacing : this.scale/40;
        // this.realDottedSpacing = this.scale/40;
        let dotted = new Values(this.realDottedSpacing, this.realDottedSpacing);
        if (this.focus) this.drawBox(
            ctx,
            0,
            undefined,
            thinBorder,
            CANVAS_ARROW_HIGHLIGHT_COLOR,
            undefined,
            0,
            undefined,
            this.theme_color.transparencyBackground,
            theme
        );
        this.drawBox(ctx, 0, undefined, thinBorder, undefined, undefined, 0, dotted, this.theme_color.transparencyBackground, theme);

        this.ownScale = (this.scale/400);
        let size = this.ownScale * 35;

        if(this.icon && this.fill){
            this.drawShapeIcon(ctx, text, theme);
        } 
        
        if(this.icon && !this.fill){
            let bounding = new ShapeBounding(this.x, this.y, size, size);
            this.drawBox(ctx, 0, undefined, 0, undefined, bounding, 0, undefined, strokeColor, theme);
            this.drawIcon(ctx, bounding, 1.1, this.theme_color.bgColor, theme);
        }

        if(!this.fill && text){
            let spacingX = this.ownScale * 10;
            let spacingY = this.ownScale * 5;
            let fontSize = this.ownScale * 20;
            let fontHeight = this.ownScale * 20;
            let textBoundingHeader = new ShapeBounding(this.x+size+spacingX, this.y+spacingY, this.width-spacingX, fontHeight);
            let calculated = this.calculateTextNoLines(text, textBoundingHeader, fontSize, false);
            this.drawText(ctx, text, calculated, textBoundingHeader, undefined, false, theme);
        }
        
    }

    private drawRhombusShape(
        ctx: CanvasRenderingContext2D,
        text: string,
        theme?: CanvasColorTheme | undefined
    ){
        if (this.focus) this.drawRhombus(ctx,  undefined, CANVAS_ARROW_HIGHLIGHT_COLOR, 3, CANVAS_ARROW_HIGHLIGHT_COLOR, undefined, undefined, undefined,undefined, theme);
        this.drawRhombus(ctx, 0, undefined, undefined, undefined, undefined, undefined, undefined, undefined, theme);
        let boudingInnerBorder = new ShapeBounding(this.x + 2, this.y + 2, this.width - 4, this.height-4);
        this.drawRhombus(ctx, 0, "#00000000", 0.4, this.theme_color.innerStrokeColor, boudingInnerBorder, this.radius/2, undefined, "#00000000", theme);
        if (text){
            let textBoundingHeader = new ShapeBounding(this.x, this.y, this.width, 30);
            this.drawStandardText(ctx, text, undefined, textBoundingHeader, theme);
        }
        // this.chooseRenderMetricsOrSimulation(ctx);
        this.renderMetrics(ctx);
        this.renderSimulation(ctx);
    }

    private drawNoGlowShape(
        ctx: CanvasRenderingContext2D,
        text: string,
        drawFunction: DrawShapeFunction,
        theme?: CanvasColorTheme | undefined
    ){
        if (this.focus) drawFunction(ctx,  undefined, CANVAS_ARROW_HIGHLIGHT_COLOR, this.borderWidth, CANVAS_ARROW_HIGHLIGHT_COLOR, undefined, undefined, undefined, undefined, theme);
        drawFunction(ctx, 0, "#00000000", this.borderWidth, undefined, undefined, undefined, undefined, undefined, theme);
        if(text){
            let textBounding = new ShapeBounding(this.x, this.y, this.width, this.height);
            let textRH = this.calculateText(ctx, text, textBounding, textBounding.width/11);
            this.drawText(ctx, text, textRH, textBounding, undefined, undefined, theme);
        }
    }

    private drawShape(
        ctx: CanvasRenderingContext2D,
        text: string,
        drawFunction: DrawShapeFunction,
        theme?: CanvasColorTheme | undefined
    ){
        // let strokeProporcion = Math.min(this.width, this.height)/40;
        let spacing = this.borderWidth;
        let boudingInnerBorder = new ShapeBounding(this.x + spacing, this.y + spacing, this.width - spacing*2, this.height-spacing*2);
        // Focus Stroke
        if (this.focus) drawFunction(ctx,  undefined, CANVAS_ARROW_HIGHLIGHT_COLOR, this.borderWidth, CANVAS_ARROW_HIGHLIGHT_COLOR, undefined, undefined, undefined, undefined, theme);
        // Outer Stroke
        drawFunction(ctx, 0, undefined, this.borderWidth, undefined, undefined, undefined, undefined, undefined, theme);
        // Inner Thin Stroke
        drawFunction(ctx, 0, "#00000000", this.borderWidth/8, this.theme_color.innerStrokeColor, boudingInnerBorder, this.radius/2, undefined, "#00000000", theme);
        if(this.icon && this.fill){
            this.drawShapeIcon(ctx, text);
        }else if(text){
            let textBounding = new ShapeBounding(this.x, this.y, this.width, this.height);
            let textBoundingHeader = new ShapeBounding(this.x + (this.width/30), this.y, this.width-10, this.height/10);
            this.drawStandardText(ctx, text, textBounding, textBoundingHeader, theme);
        }
        // this.chooseRenderMetricsOrSimulation(ctx);
        this.renderMetrics(ctx);
        this.renderSimulation(ctx);
    }

    private drawStandardText(
        ctx: CanvasRenderingContext2D,
        text: string,
        textBounding1: ShapeBounding = this.toShapeBounding(),
        headerBounding: ShapeBounding,
        theme?: CanvasColorTheme | undefined
    ){
        if(this.fill && text){
            let textRH = this.calculateText(ctx, text, textBounding1);
            this.drawText(ctx, text, textRH, textBounding1, undefined, undefined, theme);
        }
        if(!this.fill && text){
            let textHT = this.calculateText(ctx, text, headerBounding, undefined, false);
            this.drawText(ctx, text, textHT, headerBounding, undefined, false, theme);
        }
        
    }

    private drawShapeIcon(
        ctx: CanvasRenderingContext2D,
        text: string,
        theme?: CanvasColorTheme | undefined
    ){
        let iWidth = this.width / 1.5;
        let iHeight = this.height / 1.5;
        let innerBounding = new ShapeBounding(this.x + (this.width / 2) - (iWidth / 2), this.y + (this.height / 2) - (iHeight / 2), iWidth, iHeight);
        let portion = (iHeight / 3);
        let proporcion = portion * 2;
        let iconBounding = new ShapeBounding(innerBounding.x, innerBounding.y, iWidth, proporcion);
        this.drawIcon(ctx, iconBounding, 1, undefined, theme);
        if(text){
            let textBounding = new ShapeBounding(this.x, innerBounding.y + proporcion, this.width, this.y - innerBounding.y + (this.height - iHeight + portion));
            let textRH = this.calculateText(ctx, text, textBounding, textBounding.width/8);
            this.drawText(ctx, text, textRH, textBounding, undefined, undefined, theme);
        }
    }

    private drawSimpleShape(
        ctx: CanvasRenderingContext2D, 
        text: string, 
        drawFunction: DrawShapeFunction, 
        reduce: number, 
        reduceIcon: number = 1.7,
        theme?: CanvasColorTheme | undefined
    ){
        let widthRH = this.width / reduce;
        let heightRH = this.height / reduce;
        let textSpacing = (text) ? this.height : heightRH;
        this.currentBounding = new ShapeBounding(this.x + ((this.width / 2) - (widthRH / 2)), this.y, widthRH, textSpacing);
        let shapeBoundin = new ShapeBounding(this.x + ((this.width / 2) - (widthRH / 2)), this.y, widthRH, heightRH);
        if (this.focus) drawFunction(ctx, undefined, CANVAS_ARROW_HIGHLIGHT_COLOR, undefined, CANVAS_ARROW_HIGHLIGHT_COLOR, shapeBoundin, undefined, undefined, undefined, theme);
        drawFunction(ctx, 0, undefined, undefined, undefined, shapeBoundin, undefined, undefined, undefined, theme);
        let spacingX = (this.scale/100) * 2;
        let spacingY = (this.scale/100) * 4;
        let thinBorder = (this.scale/100) * 0.4;
        let boudingInnerBorder = new ShapeBounding(shapeBoundin.x + spacingX, shapeBoundin.y + spacingX, shapeBoundin.width - spacingY, shapeBoundin.height-spacingY);
        drawFunction(ctx, 0, "#00000000", thinBorder, this.theme_color.innerStrokeColor, boudingInnerBorder, this.radius/2, undefined, "#00000000", theme);
        this.drawIcon(ctx, shapeBoundin, reduceIcon, undefined, theme);
        if(text){
            let marginY = this.height*(4/100);
            let textBounding = new ShapeBounding(this.x, this.y + widthRH + marginY, this.width, this.height - heightRH - marginY);
            let textRH = this.calculateText(ctx, text, textBounding, textBounding.width/9, false);
            // this.drawBox(ctx, 0, undefined, 2, "red", textBounding, undefined, undefined, "#00000000");
            // textRH.startY = shapeBoundin.y + shapeBoundin.height + textRH.fontSize;
            this.drawText(ctx, text, textRH, textBounding, undefined, undefined, theme);
        }
        // this.chooseRenderMetricsOrSimulation(ctx);
        this.renderMetrics(ctx);
        this.renderSimulation(ctx);
    }

    private drawPeopleHex(
        ctx: CanvasRenderingContext2D, 
        name: string,
        position: string, 
        theme?: CanvasColorTheme | undefined
    ) {
        let reduceIcon = 1.5;
        let widthRH = this.width / reduceIcon;
        let heightRH = this.height / reduceIcon;
        let shapeBounding = new ShapeBounding(this.x + ((this.width / 2) - (widthRH / 2)), this.y, widthRH, heightRH);
        let marginY = this.height * (5 / 100);
        let subShape = new ShapeBounding(this.x, this.y + heightRH - marginY, this.width, this.height - heightRH);
        if (this.focus) {
            this.drawPoints(ctx, undefined, CANVAS_ARROW_HIGHLIGHT_COLOR, undefined, CANVAS_ARROW_HIGHLIGHT_COLOR, undefined, this.calculateLargeHexPoints(subShape), theme);
            this.drawHexagon(ctx, undefined, CANVAS_ARROW_HIGHLIGHT_COLOR, undefined, CANVAS_ARROW_HIGHLIGHT_COLOR, shapeBounding, undefined, undefined, undefined, theme);
        }
        this.drawPoints(ctx, 0, undefined, undefined, undefined, undefined, this.calculateLargeHexPoints(subShape), theme);
        this.drawHexagon(ctx, 0, undefined, undefined, undefined, shapeBounding, undefined, undefined, undefined, theme);
        this.ownScale = this.scale/100;
        let spacingX = this.ownScale * 2;
        let spacingY = this.ownScale * 4;
        let innerBorderThickness = this.ownScale * 0.4;
        let boudingInnerBorder = new ShapeBounding(shapeBounding.x + spacingX, shapeBounding.y + spacingX, shapeBounding.width - spacingY, shapeBounding.height - spacingY);
        //inner border
        this.drawHexagon(ctx, 0, "#00000000", innerBorderThickness, this.theme_color.innerStrokeColor, boudingInnerBorder, this.radius / 2, undefined, "#00000000", theme);
        this.drawIcon(ctx, shapeBounding, reduceIcon, undefined, theme);

        let marginX = this.width * (15 / 100);

        if (position) {
            let textBounding = new ShapeBounding(subShape.x + (marginX / 2), subShape.y, subShape.width - marginX, subShape.height);
            let nameHeight = textBounding.height / 1.6;
            let positionHeight = textBounding.height - nameHeight;
            let curMarginY = textBounding.height * (10 / 100);
            let nameBounding = new ShapeBounding(textBounding.x, textBounding.y, textBounding.width, nameHeight);
            let positionBounding = new ShapeBounding(textBounding.x, textBounding.y + nameHeight - curMarginY, textBounding.width, positionHeight);
            // this.drawText(ctx, name, this.calculateText(ctx, name, nameBounding), nameBounding);
            // this.drawText(ctx, position, this.calculateText(ctx, position, positionBounding), positionBounding);
            this.drawText(ctx, name, this.calculateTextNoLines(name, nameBounding, nameBounding.width / 6), nameBounding, "white", undefined, theme);
            this.drawText(ctx, position, this.calculateTextNoLines(position, positionBounding, nameBounding.width / 9.5), positionBounding, undefined, undefined, theme);
            return;
        }
        let textBounding = new ShapeBounding(subShape.x + (marginX / 2), subShape.y, subShape.width - marginX, subShape.height);
        let textRH = this.calculateText(ctx, name, textBounding);
        this.drawText(ctx, name, textRH, textBounding, undefined, undefined, theme);
    }

    private drawIcon(
        ctx: CanvasRenderingContext2D, 
        bounding: ShapeBounding, 
        reduce: number = 1, 
        color: string = this.theme_color.strokeColor,
        theme?: CanvasColorTheme | undefined
    ) {
        if (!this.icon) return;
        for (const shapeIcon of this.icon) {
            ctx.save();
            const reduceScaleX = bounding!.width / reduce;
            const reduceScaleY = bounding!.height / reduce;
            const scale = Math.min(reduceScaleX / shapeIcon.width, reduceScaleY / shapeIcon.height);
            const finalSizeX = shapeIcon.width * scale;
            const finalSizeY = shapeIcon.height * scale;
            ctx.translate(
                (bounding!.x + (bounding!.width / 2)) - (finalSizeX / 2),
                (bounding!.y + (bounding!.height / 2)) - (finalSizeY / 2)
            );
            ctx.scale(scale, scale);
            for (const icon of shapeIcon.icon) {
                let path = new Path2D(icon.path);
                if (icon.type === "fill") {
                    ctx.fillStyle = theme ? theme.strokeColor : color;
                    ctx.fill(path);
                } else {
                    ctx.strokeStyle = theme ? theme.strokeColor : color;
                    ctx.lineWidth = 1.5;
                    ctx.stroke(path);
                }
            }
            ctx.restore();
        }
    }

    private drawInnerBorder = (
        ctx: CanvasRenderingContext2D,
        stroke: number = 0.4,
        strokeColor: string = this.theme_color.innerStrokeColor,
        bounding: ShapeBounding = this.toShapeBounding(),
        radius: number = this.radius,
        // snip: number = 2,
        theme?: CanvasColorTheme | undefined
    ) => {
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(bounding.x, bounding.y);
        ctx.arcTo(bounding.x, bounding.y + bounding.height, bounding.x + bounding.width, bounding.y + bounding.height, radius);
        ctx.arcTo(bounding.x + bounding.width, bounding.y + bounding.height, bounding.x + bounding.width, bounding.y,  radius);
        ctx.lineTo(bounding.x + bounding.width, bounding.y);
        
        // ctx.closePath();

        ctx.strokeStyle = theme ? theme.strokeColor : strokeColor;
        ctx.lineWidth = stroke;
        ctx.stroke();

        ctx.restore();
    }

    private drawSolidBox: DrawShapeFunction = (
        ctx: CanvasRenderingContext2D,
        glow: number = 13,
        glowColor: string = this.theme_color.glowColor,
        stroke: number = this.borderWidth,
        strokeColor: string = this.theme_color.strokeColor,
        bounding: ShapeBounding = this.toShapeBounding(),
        radius: number = this.radius,
        lineType: Values = new Values(0, 0),
        backgroundColor: string = this.theme_color.bgColor,
        theme?: CanvasColorTheme | undefined
    )=> {
        ctx.save();
        ctx.shadowBlur = glow;
        ctx.shadowColor = theme ? theme.glowColor : glowColor;
        ctx.beginPath();
        ctx.moveTo(bounding.x + radius, bounding.y);
        ctx.arcTo(bounding.x + bounding.width, bounding.y, bounding.x + bounding.width, bounding.y + bounding.height, radius);
        ctx.arcTo(bounding.x + bounding.width, bounding.y + bounding.height, bounding.x, bounding.y + bounding.height, radius);
        ctx.arcTo(bounding.x, bounding.y + bounding.height, bounding.x, bounding.y, radius);
        ctx.arcTo(bounding.x, bounding.y, bounding.x + bounding.width, bounding.y, radius);
        ctx.closePath();
        ctx.setLineDash([lineType.x, lineType.y]);
        ctx.strokeStyle = backgroundColor;
        ctx.lineWidth = stroke;
        ctx.stroke();

        ctx.fillStyle = theme ? theme.bgColor : backgroundColor;
        ctx.fill();

        ctx.restore();
    }

    private drawBox: DrawShapeFunction = (
        ctx: CanvasRenderingContext2D,
        glow: number = 13,
        glowColor: string = this.theme_color.glowColor,
        stroke: number = this.borderWidth,
        strokeColor: string = this.theme_color.strokeColor,
        bounding: ShapeBounding = this.toShapeBounding(),
        radius: number = this.radius,
        lineType: Values = new Values(0, 0),
        backgroundColor: string = this.theme_color.bgColor,
        theme?: CanvasColorTheme | undefined
    )=> {
        ctx.save();
        ctx.shadowBlur = glow;
        ctx.shadowColor = theme ? theme.glowColor : glowColor;
        ctx.beginPath();
        ctx.moveTo(bounding.x + radius, bounding.y);
        ctx.arcTo(bounding.x + bounding.width, bounding.y, bounding.x + bounding.width, bounding.y + bounding.height, radius);
        ctx.arcTo(bounding.x + bounding.width, bounding.y + bounding.height, bounding.x, bounding.y + bounding.height, radius);
        ctx.arcTo(bounding.x, bounding.y + bounding.height, bounding.x, bounding.y, radius);
        ctx.arcTo(bounding.x, bounding.y, bounding.x + bounding.width, bounding.y, radius);
        ctx.closePath();
        ctx.setLineDash([lineType.x, lineType.y]);
        ctx.strokeStyle = theme ? theme.strokeColor : strokeColor;
        ctx.lineWidth = stroke;
        ctx.stroke();

        ctx.fillStyle = theme ? theme.bgColor : backgroundColor;
        ctx.fill();

        ctx.restore();
    }

    private drawHeaderBox(
        ctx: CanvasRenderingContext2D,
        stroke: number = 3,
        strokeColor: string = this.theme_color.strokeColor,
        totalHeaderHeight: number,
        theme?: CanvasColorTheme | undefined
    ){
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(this.x + this.radius, this.y);
        ctx.lineTo(this.x + this.width - this.radius, this.y);
        ctx.arcTo(this.x + this.width, this.y, this.x + this.width, this.y + this.radius, this.radius);
        ctx.lineTo(this.x + this.width, this.y + totalHeaderHeight);
        ctx.lineTo(this.x, this.y + totalHeaderHeight);
        ctx.lineTo(this.x, this.y + this.radius);
        ctx.arcTo(this.x, this.y, this.x + this.radius, this.y, this.radius);
        ctx.closePath();
        ctx.strokeStyle = theme ? theme.strokeColor : strokeColor;
        ctx.lineWidth = stroke;
        ctx.stroke();

        ctx.fillStyle = theme ? theme.headerColor : this.theme_color.headerColor;
        ctx.fill();

        ctx.restore();
    }

    private drawCircle: DrawShapeFunction = (
        ctx: CanvasRenderingContext2D,
        glow: number = 13,
        glowColor: string = this.theme_color.glowColor,
        stroke: number = this.borderWidth,
        strokeColor: string = this.theme_color.strokeColor,
        bounding: ShapeBounding = this.toShapeBounding(),
        radius: number = this.radius,
        lineType: Values = new Values(0, 0),
        backgroundColor: string = this.theme_color.bgColor,
        theme?: CanvasColorTheme | undefined
    )=>{
        ctx.save();

        ctx.shadowBlur = glow;
        ctx.shadowColor = theme ? theme.glowColor : glowColor;
        ctx.beginPath();
        ctx.ellipse(bounding.x + (bounding.width/2), bounding.y + (bounding.height/2), bounding.width / 2, bounding.height / 2, 0, 0, 2 * Math.PI);
        ctx.strokeStyle = theme ? theme.strokeColor : strokeColor;
        ctx.lineWidth = stroke;
        ctx.stroke();
        ctx.fillStyle = theme ? theme.bgColor : backgroundColor;
        ctx.fill();

        ctx.restore();
    }

    private drawHexagon: DrawShapeFunction = (
        ctx: CanvasRenderingContext2D,
        glow: number = 13,
        glowColor: string = this.theme_color.glowColor,
        stroke: number = this.borderWidth,
        strokeColor: string = this.theme_color.strokeColor,
        bounding: ShapeBounding = this.toShapeBounding(),
        radius: number = this.radius,
        lineType: Values = new Values(0, 0),
        backgroundColor: string = this.theme_color.bgColor,
        theme?: CanvasColorTheme | undefined
    )=>{
        let points = this.calculateHexPoints(bounding, new Values(bounding.x, bounding.y));
        this.drawPoints(ctx, glow, glowColor, stroke, strokeColor, backgroundColor, points, theme);
    }

    private drawPoints(
        ctx: CanvasRenderingContext2D,
        glow: number = 13,
        glowColor: string = this.theme_color.glowColor,
        stroke: number = this.borderWidth,
        strokeColor: string = this.theme_color.strokeColor,
        backgroundColor: string = this.theme_color.bgColor,
        points: Values[],
        theme?: CanvasColorTheme | undefined
    ){
        ctx.save();
        ctx.shadowBlur = glow;
        ctx.shadowColor = theme ? theme.glowColor : glowColor;
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y); // Move to the first vertex
        for (let i = 1; i < points.length; i++) {
            ctx.lineTo(points[i].x, points[i].y); // Draw lines to the subsequent vertices
        }
        ctx.closePath();
        ctx.strokeStyle = theme ? theme.strokeColor : strokeColor;
        ctx.lineWidth = stroke;
        ctx.stroke();
        ctx.fillStyle = theme ? theme.bgColor : backgroundColor;
        ctx.fill();

        ctx.restore();
    }

    private drawRhombus: DrawShapeFunction =(
        ctx: CanvasRenderingContext2D,
        glow: number = 13,
        glowColor: string = this.theme_color.glowColor,
        stroke: number = 3,
        strokeColor: string = this.theme_color.strokeColor,
        bounding: ShapeBounding = this.toShapeBounding(),
        radius: number = this.radius,
        lineType: Values = new Values(0, 0),
        backgroundColor: string = this.theme_color.bgColor,
        theme?: CanvasColorTheme | undefined
    )=>{
        let points = this.calculateRhombusPoints(bounding, new Values(bounding.x, bounding.y));
        const borderRadius = 10;

        ctx.save();
        ctx.shadowBlur = glow;
        ctx.shadowColor = theme ? theme.glowColor : glowColor;
        ctx.beginPath();

        ctx.moveTo(points[0].x + borderRadius, points[0].y + borderRadius);
        // Top-right corner
        ctx.arcTo(points[1].x, points[1].y, points[2].x, points[2].y, borderRadius);

        // Bottom-right corner
        ctx.arcTo(points[2].x, points[2].y, points[3].x, points[3].y, borderRadius);

        // Bottom-left corner
        ctx.arcTo(points[3].x, points[3].y, points[0].x, points[0].y, borderRadius);

        // Top-left corner
        ctx.arcTo(points[0].x, points[0].y, points[1].x, points[1].y, borderRadius);

        ctx.closePath();
        ctx.strokeStyle = theme ? theme.strokeColor : strokeColor;
        ctx.lineWidth = stroke;
        ctx.stroke();
        ctx.fillStyle = theme ? theme.strokeColor : backgroundColor;
        ctx.fill();

        ctx.restore();
    }

    private drawRomboidLeft: DrawShapeFunction = (
        ctx: CanvasRenderingContext2D,
        glow: number = 13,
        glowColor: string = this.theme_color.glowColor,
        stroke: number = this.borderWidth,
        strokeColor: string = this.theme_color.strokeColor,
        bounding: ShapeBounding = this.toShapeBounding(),
        radius: number = this.radius,
        lineType: Values = new Values(0, 0),
        backgroundColor: string = this.theme_color.bgColor,
        theme?: CanvasColorTheme | undefined
    ) => {
        ctx.save();
        ctx.shadowBlur = glow;
        ctx.shadowColor = theme ? theme.glowColor : glowColor;
        ctx.beginPath();

        // Romboid leaning left
        const skew = bounding.height * 0.4;
        ctx.moveTo(bounding.x + skew, bounding.y);
        ctx.lineTo(bounding.x + bounding.width, bounding.y);
        ctx.lineTo(bounding.x + bounding.width - skew, bounding.y + bounding.height);
        ctx.lineTo(bounding.x, bounding.y + bounding.height);
        ctx.closePath();

        ctx.setLineDash([lineType.x, lineType.y]);
        ctx.strokeStyle = theme ? theme.strokeColor : strokeColor;
        ctx.lineWidth = stroke;
        ctx.stroke();

        ctx.fillStyle = theme ? theme.bgColor : backgroundColor;
        ctx.fill();

        ctx.restore();
    };

    private drawRomboidRight: DrawShapeFunction = (
        ctx: CanvasRenderingContext2D,
        glow: number = 13,
        glowColor: string = this.theme_color.glowColor,
        stroke: number = this.borderWidth,
        strokeColor: string = this.theme_color.strokeColor,
        bounding: ShapeBounding = this.toShapeBounding(),
        radius: number = this.radius,
        lineType: Values = new Values(0, 0),
        backgroundColor: string = this.theme_color.bgColor,
        theme?: CanvasColorTheme | undefined
    ) => {
        ctx.save();
        ctx.shadowBlur = glow;
        ctx.shadowColor = theme ? theme.glowColor : glowColor;
        ctx.beginPath();

        // Romboid leaning right
        const skew = bounding.height * 0.4;
        ctx.moveTo(bounding.x, bounding.y);
        ctx.lineTo(bounding.x + bounding.width - skew, bounding.y);
        ctx.lineTo(bounding.x + bounding.width, bounding.y + bounding.height);
        ctx.lineTo(bounding.x + skew, bounding.y + bounding.height);
        ctx.closePath();

        ctx.setLineDash([lineType.x, lineType.y]);
        ctx.strokeStyle = theme ? theme.strokeColor : strokeColor;
        ctx.lineWidth = stroke;
        ctx.stroke();

        ctx.fillStyle = theme ? theme.bgColor : backgroundColor;
        ctx.fill();

        ctx.restore();
    };

    private drawTrapeziumLeft: DrawShapeFunction = (
        ctx: CanvasRenderingContext2D,
        glow: number = 13,
        glowColor: string = this.theme_color.glowColor,
        stroke: number = this.borderWidth,
        strokeColor: string = this.theme_color.strokeColor,
        bounding: ShapeBounding = this.toShapeBounding(),
        radius: number = this.radius,
        lineType: Values = new Values(0, 0),
        backgroundColor: string = this.theme_color.bgColor,
        theme?: CanvasColorTheme | undefined
    ) => {
        ctx.save();
        ctx.shadowBlur = glow;
        ctx.shadowColor = theme ? theme.glowColor : glowColor;
        ctx.beginPath();

        // Romboid leaning left
        const skew = bounding.height * 0.4;
        ctx.moveTo(bounding.x, bounding.y);
        ctx.lineTo(bounding.x + bounding.width, bounding.y);
        ctx.lineTo(bounding.x + bounding.width - skew, bounding.y + bounding.height);
        ctx.lineTo(bounding.x, bounding.y + bounding.height);
        ctx.closePath();

        ctx.setLineDash([lineType.x, lineType.y]);
        ctx.strokeStyle = theme ? theme.strokeColor : strokeColor;
        ctx.lineWidth = stroke;
        ctx.stroke();

        ctx.fillStyle = theme ? theme.bgColor : backgroundColor;
        ctx.fill();

        ctx.restore();
    };

    private drawTrapeziumRight: DrawShapeFunction = (
        ctx: CanvasRenderingContext2D,
        glow: number = 13,
        glowColor: string = this.theme_color.glowColor,
        stroke: number = this.borderWidth,
        strokeColor: string = this.theme_color.strokeColor,
        bounding: ShapeBounding = this.toShapeBounding(),
        radius: number = this.radius,
        lineType: Values = new Values(0, 0),
        backgroundColor: string = this.theme_color.bgColor,
        theme?: CanvasColorTheme | undefined
    ) => {
        ctx.save();
        ctx.shadowBlur = glow;
        ctx.shadowColor = theme ? theme.glowColor : glowColor;
        ctx.beginPath();

        // Romboid leaning right
        const skew = bounding.height * 0.4;
        ctx.moveTo(bounding.x, bounding.y);
        ctx.lineTo(bounding.x + bounding.width - skew, bounding.y);
        ctx.lineTo(bounding.x + bounding.width, bounding.y + bounding.height);
        ctx.lineTo(bounding.x, bounding.y + bounding.height);
        ctx.closePath();

        ctx.setLineDash([lineType.x, lineType.y]);
        ctx.strokeStyle = theme ? theme.strokeColor : strokeColor;
        ctx.lineWidth = stroke;
        ctx.stroke();

        ctx.fillStyle = theme ? theme.bgColor : backgroundColor;
        ctx.fill();

        ctx.restore();
    };

    private calculateTextNoLines(text: string, bounding: ShapeBounding = this.toShapeBounding(), size: number = bounding.width/7, centered: boolean = true): HeaderTextValues{
        let originalSize = size;
        const fontSize = originalSize;
        const availableWidth = bounding.width - 2 * this.textPadding;
        const lines = [text];
        const lineHeight = fontSize * 1.2;
        let totalHeight = lineHeight;
        let startY = bounding.y + (bounding.height - totalHeight) / 2 + fontSize;
        if(!centered) {
            // startY = this.y + this.headerPaddingHeight + fontSize;
            // totalHeight = totalHeight + (this.headerPaddingHeight*2);
            startY = bounding.y + fontSize;
        }

        return { fontSize, startY, lines, lineHeight, totalHeight, availableWidth } as HeaderTextValues;
    }

    private calculateText(ctx: CanvasRenderingContext2D, text: string, bounding: ShapeBounding = this.toShapeBounding(), size: number = bounding.width/7, centered: boolean = true): HeaderTextValues{
        return this.calculateFittedText(ctx, text, bounding, size, centered);
    }

    /**
     * Text is a visual representation of the canonical name, never a shorter
     * replacement for it. Prefer a complete wrapped label and progressively
     * reduce the font before falling back to an ellipsis.
     */
    private calculateFittedText(
        ctx: CanvasRenderingContext2D,
        text: string,
        bounding: ShapeBounding,
        preferredSize: number,
        centered: boolean,
        rotated: boolean = false
    ): HeaderTextValues {
        ctx.save();
        try {
        const inlineLength = rotated ? bounding.height : bounding.width;
        const blockLength = rotated ? bounding.width : bounding.height;
        // Header bands may be smaller than the card's body padding.
        const padding = Math.min(this.textPadding, inlineLength * 0.05, blockLength * 0.05);
        const availableWidth = Math.max(1, inlineLength - 2 * padding);
        const availableHeight = Math.max(1, blockLength - 2 * padding);
        const constrainedPreferredSize = Math.max(0.01, Math.min(preferredSize, availableHeight / 1.2));
        const minimumSize = Math.min(constrainedPreferredSize, Math.max(8, constrainedPreferredSize * 0.45));
        let fontSize = constrainedPreferredSize;
        let lines: string[] = [];

        // Font reduction is deliberately tried before truncation. This handles
        // long regulatory/process labels without relying on image-specific
        // exceptions or making wider cards unexpectedly use a larger font.
        while (fontSize >= minimumSize) {
            ctx.font = `${CANVAS_ITEM_TEXT_FONT_WEIGHT} ${fontSize}px ${CANVAS_ITEM_TEXT_FONT}`;
            lines = this.wrapText(ctx, text, availableWidth);
            const lineHeight = fontSize * 1.2;
            if (lines.length * lineHeight <= availableHeight) break;
            fontSize = Math.max(minimumSize, fontSize - 1);
            if (fontSize === minimumSize) {
                ctx.font = `${CANVAS_ITEM_TEXT_FONT_WEIGHT} ${fontSize}px ${CANVAS_ITEM_TEXT_FONT}`;
                lines = this.wrapText(ctx, text, availableWidth);
                break;
            }
        }

        const lineHeight = fontSize * 1.2;
        const maximumLines = Math.max(1, Math.floor(availableHeight / lineHeight));
        if (lines.length > maximumLines) {
            lines = this.wrapText(ctx, text, availableWidth, maximumLines);
        }

        let totalHeight = lines.length * lineHeight;
        const origin = rotated ? bounding.y : bounding.y;
        let startY = origin + (blockLength - totalHeight) / 2 + fontSize;
        if (!centered) {
            startY = origin + fontSize;
            totalHeight += this.headerPaddingHeight * 2;
        }

        return { fontSize, startY, lines, lineHeight, totalHeight, availableWidth } as HeaderTextValues;
        } finally {
            ctx.restore();
        }
    }

    private drawText(
        ctx: CanvasRenderingContext2D,
        text: string,
        textValues?: HeaderTextValues,
        bounding: ShapeBounding = this.toShapeBounding(),
        textColor: string = this.theme_color.textColor,
        centered: boolean = true,
        theme?: CanvasColorTheme | undefined
    ) {
        // let text = this.name || this.id.replaceAll("-", " ");
        if (text) {
            ctx.save();
            try {
            let { fontSize, startY, lines, lineHeight, availableWidth } = textValues || this.calculateText(ctx, text);
            ctx.font = `${CANVAS_ITEM_TEXT_FONT_WEIGHT} ${fontSize}px ${CANVAS_ITEM_TEXT_FONT}`;
            ctx.textAlign = 'left';
            ctx.textBaseline = 'alphabetic';
            const textClip = new Path2D();
            textClip.rect(bounding.x, bounding.y, bounding.width, bounding.height);
            ctx.clip(textClip);
            ctx.fillStyle = theme ? theme.strokeColor : textColor;
            // Draw each line of text
            lines.forEach((line, index) => {
                const textWidth = ctx.measureText(line).width;
                let textX = bounding.x + (bounding.width - textWidth) / 2;
                if(!centered) textX = bounding.x;
                // if(!centered) textX = this.x + this.headerPaddingWidth;
                // const textX = bounding.x + this.textPadding + (availableWidth - textWidth) / 2;
                const textY = startY + index * lineHeight;
                ctx.fillText(line, textX, textY);
            });
            } finally {
                ctx.restore();
            }
        }
    }

    private calculateTextRotated(
        ctx: CanvasRenderingContext2D,
        text: string,
        bounding: ShapeBounding = this.toShapeBounding(),
        size: number = bounding.height/7, // Use height for vertical text
        centered: boolean = true
    ): HeaderTextValues {
        return this.calculateFittedText(ctx, text, bounding, size, centered, true);
    }

    private drawTextRotated(
        ctx: CanvasRenderingContext2D,
        text: string,
        textValues?: HeaderTextValues,
        bounding: ShapeBounding = this.toShapeBounding(),
        textColor: string = this.theme_color.textColor,
        centered: boolean = true,
        theme?: CanvasColorTheme | undefined
    ) {
        if (text) {
            let { fontSize, startY, lines, lineHeight, availableWidth } = textValues || this.calculateTextRotated(ctx, text, bounding, undefined, centered);
            ctx.save();
            ctx.font = `${CANVAS_ITEM_TEXT_FONT_WEIGHT} ${fontSize}px ${CANVAS_ITEM_TEXT_FONT}`;
            ctx.fillStyle = theme ? theme.strokeColor : textColor;
            ctx.textAlign = 'left';
            ctx.textBaseline = 'alphabetic';

            // Translate to the bottom left of the bounding box, then rotate
            ctx.translate(bounding.x, bounding.y + bounding.height);
            ctx.rotate(-Math.PI / 2);
            lines.forEach((line, index) => {
                const textWidth = ctx.measureText(line).width;
                // Now X is vertical, Y is horizontal after rotation
                let textX = this.textPadding + (availableWidth - textWidth) / 2;
                if (!centered) textX = 0;
                const textY = startY + index * lineHeight - bounding.y;
                ctx.fillText(line, textX, textY);
            });
            ctx.restore();
        }
    }

    private drawHandle(ctx: CanvasRenderingContext2D){
        ctx.save();
        const handleSpacing = this.radius; // Distance between the handles
        // const handleSpacing = this.width/33; // Distance between the handles
        // const handleSpacing = Math.min(this.width/33, this.height/33); 

        ctx.strokeStyle = this.theme_color.strokeColor;
        // ctx.lineWidth = this.width/100;
        // ctx.lineWidth = Math.min(this.width/100, this.height/100);
        // ctx.lineWidth = ((this.width + this.height)/2)/100;
        ctx.lineWidth = this.radius/5;

        // First inner curve, shifted towards the center by handleSpacing
        ctx.beginPath();
        ctx.arc(
          this.x + this.width - this.radius - handleSpacing,
          this.y + this.height - this.radius - handleSpacing,
          this.radius,
          0,
          Math.PI / 2,
          false
        );
        ctx.stroke();
        ctx.restore();
    }
}
